import { EditOutlined } from '@ant-design/icons'
import { Button, Flex, Typography, notification } from 'antd'
import { FC, useState } from 'react'
import styled from 'styled-components'
import PhoneControl from '../../../../components/custom-form-controls/phone-control/phone-control'
import { useUpdateUserMutation } from '../../../../store/api/users-api'

const HeaderWrapper = styled.div`
    padding-bottom: 8px;
    border-bottom: 1px solid ${p => p.theme.colorBorderSecondary};
`

const PositionText = styled(Typography.Text)`
    font-size: 16px;
`

const PhoneRow = styled(Flex)`
    margin-top: 8px;
`

const PhoneInputWrap = styled.div`
    width: 220px;
`

interface ProfileHeaderProps {
    userId?: number
    username: string
    phoneNumber?: string
    positionName: string
}

const PHONE_NUMBER_REGEX = /^7\d{10}$/
const PHONE_VALIDATION_MESSAGE = 'Введите телефон в формате +7 (999) 999-99-99'

const getPhoneDigits = (value?: string): string => value?.replace(/\D/g, '') ?? ''

const toPhoneNumberForSave = (value?: string): string => {
    const digits = getPhoneDigits(value)
    if (digits.length === 11 && digits.startsWith('8')) return `7${digits.slice(1)}`
    if (digits.length === 10) return `7${digits}`
    return digits
}

const formatPhoneNumber = (value?: string): string => {
    if (!value) return '—'

    const normalized = toPhoneNumberForSave(value)
    if (!PHONE_NUMBER_REGEX.test(normalized)) return value

    const phone = normalized.slice(1)
    return `+7 (${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6, 8)}-${phone.slice(8, 10)}`
}

const ProfileHeader: FC<ProfileHeaderProps> = ({ userId, username, phoneNumber, positionName }) => {
    const [isEditingPhone, setIsEditingPhone] = useState(false)
    const [phoneDraft, setPhoneDraft] = useState('')
    const [updateUser, updateUserState] = useUpdateUserMutation()

    const formattedPhone = formatPhoneNumber(phoneNumber)

    const onStartEditPhone = () => {
        setPhoneDraft(formatPhoneNumber(phoneNumber))
        setIsEditingPhone(true)
    }

    const onSavePhone = async () => {
        const normalizedPhone = toPhoneNumberForSave(phoneDraft)

        if (!PHONE_NUMBER_REGEX.test(normalizedPhone)) {
            notification.error({ message: PHONE_VALIDATION_MESSAGE })
            return
        }

        if (!userId) {
            notification.error({ message: 'Не удалось определить пользователя для сохранения телефона' })
            return
        }

        try {
            await updateUser({ id: userId, phoneNumber: normalizedPhone }).unwrap()
            setIsEditingPhone(false)
            notification.success({ message: 'Телефон сохранен' })
        } catch {
            notification.error({ message: 'Не удалось сохранить телефон' })
        }
    }

    return (
        <HeaderWrapper>
            <Typography.Title level={3}>{username}</Typography.Title>
            <PositionText type='secondary'>{positionName}</PositionText>
            <PhoneRow align='center' gap={8}>
                <Typography.Text>Телефон:</Typography.Text>

                {isEditingPhone ? (
                    <>
                        <PhoneInputWrap>
                            <PhoneControl value={phoneDraft} onChange={setPhoneDraft} />
                        </PhoneInputWrap>
                        <Button type='primary' loading={updateUserState.isLoading} onClick={onSavePhone}>
                            Сохранить
                        </Button>
                    </>
                ) : (
                    <>
                        <Typography.Text>{formattedPhone}</Typography.Text>
                        <Button
                            type='text'
                            icon={<EditOutlined />}
                            aria-label='Редактировать телефон'
                            onClick={onStartEditPhone}
                        />
                    </>
                )}
            </PhoneRow>
        </HeaderWrapper>
    )
}

export default ProfileHeader
